import { useLang } from "@/lib/i18n";
import { useSettings } from "@/lib/useSettings";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Briefcase, Shield } from "lucide-react";
import { addContactMessage } from "@/api/contact";
import { SEO } from "@/components/site/SEO";

export function ContactPage() {
  const { lang, t } = useLang();
  const { settings } = useSettings();
  const c = settings.company ?? {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: "" });

  let waNumber = "905303385529";
  if (c.whatsapp) {
    let cleaned = c.whatsapp.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '90' + cleaned.substring(1);
    } else if (cleaned.length === 10 && cleaned.startsWith('5')) {
      cleaned = '90' + cleaned;
    }
    if (cleaned.length > 0) {
      waNumber = cleaned;
    }
  }

  let mapLink = c.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((c.name || '') + ' ' + c.address)}` : undefined;
  if (c.map_url && c.map_url.includes('pb=')) {
    const cidMatch = c.map_url.match(/!1s0x[0-9a-fA-F]+(?:%3A|:)(0x[0-9a-fA-F]+)/);
    if (cidMatch && cidMatch[1]) {
      try {
        const cid = BigInt(cidMatch[1]).toString();
        mapLink = `https://www.google.com/maps?cid=${cid}`;
      } catch(e) {}
    }
  } else if (c.map_url && !c.map_url.includes('embed')) {
    mapLink = c.map_url;
  }

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: "" });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CAPTCHA doğrulama
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      toast.error(lang === 'tr' ? 'Güvenlik kodu yanlış!' : 'Security code is incorrect!');
      generateCaptcha();
      return;
    }

    setLoading(true);
    try {
      await addContactMessage({ ...form, captcha: captcha.answer, captchaSum: correctAnswer });
      setLoading(false);
      toast.success(t("form.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      generateCaptcha();
    } catch (error) {
      setLoading(false);
      toast.error(t("form.error"));
      generateCaptcha();
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <SEO
        title={t("section.contact")}
        description={
          lang === "tr"
            ? "Awork Danışmanlık ile iletişime geçin. Çalışma izni, oturma izni ve danışmanlık hizmetleri hakkında bilgi alın."
            : "Contact Awork Consulting. Get information about work permits, residence permits, and consulting services."
        }
        keywords={
          lang === "tr"
            ? "iletişim, danışmanlık, çalışma izni başvurusu, oturma izni danışmanlık, Awork iletişim"
            : "contact, consulting, work permit application, residence permit consulting, Awork contact"
        }
      />
      <h2 className="text-5xl font-bold text-center text-primary mb-2">{t("section.contact")}</h2>
      <div className="w-20 h-1 bg-primary mx-auto mb-12 " />
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Info icon={<Briefcase size={20} />} title={c.name ?? ""} body={c[`slogan_${lang}`]} />
          <Info icon={<MapPin size={20} />} title={t("common.address")} body={c.address} href={mapLink} />
          <Info icon={<Phone size={20} />} title={t("common.phone")} body={`${c.phone ?? ""}${c.phone2 ? " · " + c.phone2 : ""}`} href={c.phone ? `tel:${c.phone.replace(/\s+/g, '')}` : undefined} />
          <Info 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
              </svg>
            } 
            title="WhatsApp" 
            body={c.whatsapp || "0530 338 55 29"} 
            href={`https://wa.me/${waNumber}`}
          />
          <Info icon={<Mail size={20} />} title="Email" body={c.email} href={c.email ? `mailto:${c.email}` : undefined} />
          <div className="bg-card border p-5">
            <h4 className="font-bold text-primary mb-3">{t("common.followUs")}</h4>
            <div className="flex gap-3">
              {c.social?.instagram && <a href={c.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Instagram size={18} /></a>}
              {c.social?.facebook && <a href={c.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Facebook size={18} /></a>}
              {c.social?.linkedin && <a href={c.social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Linkedin size={18} /></a>}
              {c.social?.twitter && <a href={c.social.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="bg-card border p-6 space-y-4 flex flex-col h-full">
          <Inp label={t("common.name")} v={form.name} on={(v: string) => setForm({ ...form, name: v })} req />
          <div className="grid sm:grid-cols-2 gap-4">
            <Inp label={t("common.email")} type="email" v={form.email} on={(v: string) => setForm({ ...form, email: v })} req />
            <Inp label={t("common.phone")} v={form.phone} on={(v: string) => setForm({ ...form, phone: v })} />
          </div>
          <Inp label={t("common.subject")} v={form.subject} on={(v: string) => setForm({ ...form, subject: v })} />
          <div className="flex-1 flex flex-col">
            <label className="block text-sm font-medium mb-1">{t("common.message")} *</label>
            <textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full flex-1 border px-3 py-2 bg-background min-h-[120px]" />
          </div>
          <div className="bg-primary/5 border-2 border-primary/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="text-primary" size={20} />
              <label className="block text-sm font-bold text-primary">
                {lang === 'tr' ? 'Güvenlik Kodu' : 'Security Code'} *
              </label>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white border-2 border-primary px-4 py-2 font-mono text-xl font-bold text-primary">
                {captcha.num1} + {captcha.num2} = ?
              </div>
              <input 
                required 
                type="number" 
                value={captcha.answer} 
                onChange={e => setCaptcha({ ...captcha, answer: e.target.value })}
                placeholder={lang === 'tr' ? 'Cevap' : 'Answer'}
                className="w-24 border-2 border-primary px-3 py-2 bg-white text-center font-bold text-lg"
              />
            </div>
          </div>
          <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3 font-medium hover:bg-primary-dark disabled:opacity-50">{loading ? "..." : t("common.send")}</button>
        </form>
      </div>
      
      {c.map_url && (
        <div className="mt-12">
          <iframe
            src={c.map_url}
            width={c.map_width || "100%"}
            height={c.map_height || "450"}
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="bg-card border p-2"
          ></iframe>
        </div>
      )}
    </div>
  );
}

function Info({ icon, title, body, href }: any) {
  const content = (
    <>
      <div className="w-10 h-10 bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <h4 className="font-bold text-primary mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </>
  );

  if (href) {
    const isExternal = href.startsWith('http');
    return (
      <a 
        href={href} 
        target={isExternal ? "_blank" : undefined} 
        rel={isExternal ? "noreferrer" : undefined}
        className="bg-card border p-5 flex gap-4 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
      >
        {content}
      </a>
    );
  }

  return (
    <div className="bg-card border p-5 flex gap-4">
      {content}
    </div>
  );
}

function Inp({ label, v, on, type = "text", req }: any) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}{req && " *"}</label>
      <input required={req} type={type} value={v} onChange={e => on(e.target.value)} className="w-full border px-3 py-2 bg-background" />
    </div>
  );
}
